'use client'
import React, { Dispatch, FC, SetStateAction, useContext } from 'react'
import { LFButton, LFButtonColor, LFButtonType } from '@components/LFButton'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'
import { LFBounceLoader } from '@components/spinner/Spinner'
import {
  findDefaultSeason,
  getYearOption,
} from '@app/overview/_components/header'
import { isPresent } from '@farmai-shared/LFUtils'
import { useQueryOrg } from '@services/useQueryOrg'
import { RootStoreContext } from '@stores/rootStore'
import { useLFSearchParams } from '@services/useLFSearchParams'
import Cookies from 'js-cookie'

export const FiltersButton: FC<{
  setShowFilters: Dispatch<SetStateAction<boolean>>
  className?: string
}> = observer(({ setShowFilters, className = '' }) => {
  const { t } = useTranslation()
  const { uiStore, userStore } = useContext(RootStoreContext)
  const { searchParams, setParams } = useLFSearchParams()
  const lang = userStore.user.settings.lang
  const { isLoading } = useQueryOrg({
    queryKey: ['overview page seasons'],
    queryFn: () =>
      uiStore.currentOrganization?.fetchSeasons().then((seasons) => {
        if (isPresent(seasons)) {
          const defaultSeason = findDefaultSeason(seasons)

          if (
            !searchParams?.has('filter[year]') &&
            Cookies.get('selectedDefaultSeasonYearDropdown') != 'year'
          ) {
            if (!searchParams?.has('filter[season_id]')) {
              handleDefaultSetup('season_id', {
                label: defaultSeason.name,
                value: defaultSeason.id,
              })
            }
            return seasons
          }
        }

        const queryYear = searchParams?.get('filter[year]')
        handleDefaultSetup(
          'year',
          queryYear ? getYearOption(lang, queryYear) : getYearOption(lang)
        )
        return seasons
      }),
    enabled:
      !searchParams?.has('filter[season_id]') &&
      !searchParams?.has('filter[year]'),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })

  const handleDefaultSetup = (
    filter: 'season_id' | 'year',
    defaultOption: { label: string; value: string }
  ) => {
    Cookies.set('selectedDefaultSeasonYearDropdown', filter)
    handleQueryParams(filter === 'year', defaultOption)
  }

  const handleQueryParams = (
    isYearCategory: boolean,
    defaultOption: { value: string; label: string }
  ) => {
    setParams([
      {
        key: 'filter[year]',
        value: isYearCategory ? defaultOption.value : null,
      },
      {
        key: 'filter[season_id]',
        value: isYearCategory ? null : defaultOption.value,
      },
    ])
  }

  return (
    <LFButton
      disabled={isLoading}
      type={LFButtonType.PRIMARY}
      testId="overview-filters-button"
      color={LFButtonColor.NEUTRAL}
      borderRadius="rounded-xl"
      padding="px-4 py-3"
      className={`
        my-1 w-full text-center
        lg:my-0 lg:w-1/2 lg:max-w-82
        print:hidden
        ${className}
      `}
      onClick={() => {
        setShowFilters(true)
      }}
    >
      {isLoading ? (
        <LFBounceLoader size={14} isLoading />
      ) : (
        <img src={'/static/assets/filter.svg'} className={`sm:block`} />
      )}

      <p className={`text-2xl font-thin whitespace-nowrap text-lf-navy`}>
        {t('filters')}
      </p>
    </LFButton>
  )
})
